import React, { useId, useState } from "react";
import { Box } from "@mui/material";
import Section from "../ui/Section";
import { ArrowLink } from "../ui/Button";
import { Accent, Display, Label } from "../ui/Type";
import { MEDIA } from "../../data/media";
import { BEFORE_AFTER } from "../../data/projects";
import { color, font, type } from "../../design/tokens";

/**
 * Before / after comparison slider.
 *
 * The control is a real `<input type="range">` stretched invisibly over the
 * frame, with the visible handle drawn separately. That one decision buys
 * pointer drag, touch drag, click-to-jump, arrow-key stepping, Home/End, and a
 * correctly announced slider role — all native, none of it reimplemented, and
 * none of it broken by a stray pointer capture.
 *
 * Both photographs are of the same staircase from the same corner. Pairing two
 * merely similar rooms and calling it a transformation is the standard version
 * of this component and it's a lie; if a future pair can't be verified as the
 * same space, it doesn't belong here.
 */
const BeforeAfter = () => {
  const [position, setPosition] = useState(50);
  // The range input is invisible, so its focus ring has to be drawn on the
  // frame instead. Tracked in state rather than with `:has()`, which isn't
  // safe to rely on for something as load-bearing as a focus indicator.
  const [focused, setFocused] = useState(false);
  const inputId = useId();

  const before = MEDIA[BEFORE_AFTER.before];
  const after = MEDIA[BEFORE_AFTER.after];

  if (!before || !after) return null;

  const chip = (label, side) => (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        bottom: 16,
        [side]: 16,
        ...type.label,
        color: color.onInk,
        backgroundColor: "rgba(23, 21, 15, 0.72)",
        backdropFilter: "blur(6px)",
        paddingInline: 1.5,
        paddingBlock: 1,
        pointerEvents: "none",
      }}
    >
      {label}
    </Box>
  );

  return (
    <Section tone="deep">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.85fr 1.15fr" },
          gap: { xs: 5, md: 10 },
          alignItems: "center",
        }}
      >
        <Box>
          <Label>Before / after</Label>
          <Display as="h2" size="lg" sx={{ marginTop: 3 }}>
            One staircase, <Accent>start to finish</Accent>.
          </Display>
          <Box
            component="p"
            sx={{ ...type.body, color: color.graphite, marginTop: 3, maxWidth: 460 }}
          >
            {BEFORE_AFTER.description}
          </Box>
          <Box sx={{ marginTop: 4 }}>
            <ArrowLink to="/projects">See the full project</ArrowLink>
          </Box>
        </Box>

        <Box>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: { md: 560 },
              marginInline: { md: "auto" },
              aspectRatio: "4 / 5",
              overflow: "hidden",
              backgroundColor: color.paperEdge,
              userSelect: "none",
              touchAction: "pan-y",
              outline: focused ? `2px solid ${color.clay}` : "none",
              outlineOffset: "3px",
            }}
          >
            <Box
              component="img"
              src={after.src}
              alt={after.alt}
              draggable={false}
              loading="lazy"
              sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                clipPath: `inset(0 ${100 - position}% 0 0)`,
              }}
            >
              <Box
                component="img"
                src={before.src}
                alt={before.alt}
                draggable={false}
                loading="lazy"
                sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>

            {/* Divider + handle. Decorative — the range input underneath is
                what's actually focusable and announced. */}
            <Box
              aria-hidden="true"
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${position}%`,
                width: "2px",
                marginLeft: "-1px",
                backgroundColor: color.paper,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: color.paper,
                  color: color.ink,
                  display: "grid",
                  placeItems: "center",
                  fontFamily: font.body,
                  fontSize: "0.75rem",
                  letterSpacing: "0.05em",
                }}
              >
                ‹ ›
              </Box>
            </Box>

            {chip(BEFORE_AFTER.beforeLabel, "left")}
            {chip(BEFORE_AFTER.afterLabel, "right")}

            <Box
              component="label"
              htmlFor={inputId}
              sx={{
                position: "absolute",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
              }}
            >
              Reveal the before photograph
            </Box>
            <Box
              component="input"
              id={inputId}
              type="range"
              min={0}
              max={100}
              step={1}
              value={position}
              onChange={(event) => setPosition(Number(event.target.value))}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              aria-valuetext={`${position}% before, ${100 - position}% after`}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                margin: 0,
                opacity: 0,
                cursor: "ew-resize",
                appearance: "none",
                background: "transparent",
                // A tall thumb means the drag target is the whole height of
                // the frame rather than a 20px band across the middle.
                "&::-webkit-slider-thumb": {
                  appearance: "none",
                  width: "48px",
                  height: "700px",
                  cursor: "ew-resize",
                },
                "&::-moz-range-thumb": {
                  width: "48px",
                  height: "700px",
                  border: "none",
                  cursor: "ew-resize",
                },
                // Suppressed here because the ring is drawn on the frame above.
                "&:focus-visible": { outline: "none" },
              }}
            />
          </Box>

          <Box
            component="p"
            sx={{
              ...type.caption,
              color: color.mute,
              marginTop: 2,
              maxWidth: { md: 560 },
              marginInline: { md: "auto" },
              paddingLeft: 2,
              borderLeft: `1px solid ${color.line}`,
            }}
          >
            Drag the handle, or focus it and use the arrow keys.
          </Box>
        </Box>
      </Box>
    </Section>
  );
};

export default BeforeAfter;
